import { queryOptions, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  fetchCounterpartyContacts,
  fetchEvidenceRun,
  fetchOnboardingOverview,
  fetchOnboardingPhase,
  onboardingFailureRefusal,
  onboardingQueryKeys,
} from '@finnest/power-admin-client';
import type {
  CustomerId,
  OnboardingEvidenceRun,
  OnboardingListCounterpartyContactsResponse,
  OnboardingOverview,
  Phase,
  PhaseId,
} from '@finnest/power-admin-contracts';
import type { PowerRefusal } from '@finnest/power-admin-contracts/failure';
import { Match, Option } from 'effect';

import { onboardingApiOptions } from '../onboarding-api-options.ts';

const POLL_INTERVAL_MS = 30_000;

const onboardingOverviewQueryOptions = (
  customerId: CustomerId,
): ReturnType<
  typeof queryOptions<
    OnboardingOverview,
    Error,
    OnboardingOverview,
    ReturnType<typeof onboardingQueryKeys.overview>
  >
> =>
  queryOptions({
    queryKey: onboardingQueryKeys.overview(customerId),
    queryFn: () => fetchOnboardingOverview(customerId, onboardingApiOptions()),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

export const useOnboardingOverview = (customerId: CustomerId): UseQueryResult<OnboardingOverview> =>
  useQuery(onboardingOverviewQueryOptions(customerId));

const onboardingPhaseQueryOptions = (
  customerId: CustomerId,
  phaseId: PhaseId,
): ReturnType<
  typeof queryOptions<Phase, Error, Phase, ReturnType<typeof onboardingQueryKeys.phase>>
> =>
  queryOptions({
    queryKey: onboardingQueryKeys.phase(customerId, phaseId),
    queryFn: () => fetchOnboardingPhase(customerId, phaseId, onboardingApiOptions()),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

export const usePhaseDetail = (customerId: CustomerId, phaseId: PhaseId): UseQueryResult<Phase> =>
  useQuery(onboardingPhaseQueryOptions(customerId, phaseId));

const evidenceRunQueryOptions = (
  customerId: CustomerId,
  phaseDir: string,
  runId: string,
): ReturnType<
  typeof queryOptions<
    OnboardingEvidenceRun,
    Error,
    OnboardingEvidenceRun,
    ReturnType<typeof onboardingQueryKeys.evidenceRun>
  >
> =>
  queryOptions({
    queryKey: onboardingQueryKeys.evidenceRun(customerId, phaseDir, runId),
    queryFn: () => fetchEvidenceRun(customerId, phaseDir, runId, onboardingApiOptions()),
    staleTime: Infinity,
  });

export const useEvidenceRun = (
  customerId: CustomerId,
  phaseDir: string,
  runId: string,
): UseQueryResult<OnboardingEvidenceRun> =>
  useQuery(evidenceRunQueryOptions(customerId, phaseDir, runId));

const counterpartyContactsQueryOptions = (
  customerId: CustomerId,
): ReturnType<
  typeof queryOptions<
    OnboardingListCounterpartyContactsResponse,
    Error,
    OnboardingListCounterpartyContactsResponse,
    ReturnType<typeof onboardingQueryKeys.p1CounterpartyContacts>
  >
> =>
  queryOptions({
    queryKey: onboardingQueryKeys.p1CounterpartyContacts(customerId),
    queryFn: () => fetchCounterpartyContacts(customerId, onboardingApiOptions()),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

export const useCounterpartyContacts = (
  customerId: CustomerId,
): UseQueryResult<OnboardingListCounterpartyContactsResponse> =>
  useQuery(counterpartyContactsQueryOptions(customerId));

/**
 * The error code and trace id of the first failed onboarding read Power refused with a code; none
 * when no failed read names one.
 */
export const onboardingReadsRefusal = (
  reads: readonly UseQueryResult[],
): Option.Option<PowerRefusal> =>
  Option.firstSomeOf(
    reads.map((read) =>
      Match.value(read).pipe(
        Match.when({ status: 'error' }, ({ error }) => onboardingFailureRefusal(error)),
        Match.whenOr({ status: 'pending' }, { status: 'success' }, () =>
          Option.none<PowerRefusal>(),
        ),
        Match.exhaustive,
      ),
    ),
  );
